import React, { useEffect } from 'react';
import Animated, {
  cancelAnimation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';

import type { LoadMainVariantProps } from '../types';
import { LOAD_MAIN_EASING } from '../loadMainMotion';

const FRAME_PATH =
  'M8 4.5h8c0 0 1.8 2.6 4 4.8 1.4 1.4 1.4 3.2 0 4.6-2.2 2.2-4 4.8-4 4.8H8s-1.8-2.6-4-4.8c-1.4-1.4-1.4-3.2 0-4.6C6.2 7.1 8 4.5 8 4.5z';

export function HourglassLoader({ size, colors, duration_ms }: LoadMainVariantProps) {
  const flip = useSharedValue(0);
  const sand = useSharedValue(0);
  const half = Math.max(220, duration_ms * 0.48);

  useEffect(() => {
    flip.value = 0;
    sand.value = 0;
    flip.value = withRepeat(
      withSequence(
        withTiming(1, { duration: half, easing: LOAD_MAIN_EASING.soft }),
        withTiming(2, { duration: half, easing: LOAD_MAIN_EASING.soft }),
      ),
      -1,
      false,
    );
    sand.value = withRepeat(
      withSequence(
        withTiming(1, { duration: half, easing: LOAD_MAIN_EASING.smooth }),
        withTiming(0, { duration: half, easing: LOAD_MAIN_EASING.smooth }),
      ),
      -1,
      false,
    );
    return () => {
      cancelAnimation(flip);
      cancelAnimation(sand);
    };
  }, [duration_ms, flip, half, sand]);

  const frame_style = useAnimatedStyle(() => {
    const turn = interpolate(flip.value, [0, 1, 2], [0, 180, 360]);
    const breathe = interpolate(flip.value - Math.floor(flip.value), [0, 0.5, 1], [1, 0.94, 1]);
    return {
      transform: [{ rotate: `${turn}deg` }, { scale: breathe }],
    };
  });

  const top_sand_style = useAnimatedStyle(() => ({
    opacity: interpolate(sand.value, [0, 1], [0.95, 0.2]),
    transform: [{ scaleY: interpolate(sand.value, [0, 1], [1, 0.15]) }],
  }));

  const bottom_sand_style = useAnimatedStyle(() => ({
    opacity: interpolate(sand.value, [0, 1], [0.2, 0.95]),
    transform: [{ scaleY: interpolate(sand.value, [0, 1], [0.15, 1]) }],
  }));

  const stream_style = useAnimatedStyle(() => ({
    opacity: interpolate(sand.value, [0, 0.5, 1], [0.15, 1, 0.15]),
    transform: [{ scaleY: interpolate(sand.value, [0, 0.5, 1], [0.4, 1, 0.4]) }],
  }));

  const sand_width = size * 0.22;
  const sand_height = size * 0.14;

  return (
    <Animated.View style={[{ width: size, height: size }, frame_style]}>
      <Svg width={size} height={size} viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
        <Path
          d={FRAME_PATH}
          stroke={colors.primary}
          strokeWidth={1.35}
          fill={colors.glow}
          strokeLinejoin="round"
        />
      </Svg>
      <Animated.View
        style={[
          {
            position: 'absolute',
            top: size * 0.2,
            left: (size - sand_width) / 2,
            width: sand_width,
            height: sand_height,
            borderRadius: sand_width * 0.2,
            backgroundColor: colors.primary,
          },
          top_sand_style,
        ]}
      />
      <Animated.View
        style={[
          {
            position: 'absolute',
            top: size * 0.46,
            left: (size - sand_width * 0.18) / 2,
            width: sand_width * 0.18,
            height: sand_height * 0.95,
            borderRadius: sand_width * 0.09,
            backgroundColor: colors.highlight,
          },
          stream_style,
        ]}
      />
      <Animated.View
        style={[
          {
            position: 'absolute',
            bottom: size * 0.2,
            left: (size - sand_width) / 2,
            width: sand_width,
            height: sand_height,
            borderRadius: sand_width * 0.2,
            backgroundColor: colors.primary,
          },
          bottom_sand_style,
        ]}
      />
    </Animated.View>
  );
}
